import { nextTick } from 'vue'
import type { HomeGetMusicDetailResponse, ProgressResponse } from '~/types/app'
import type { GeneratePrefillPayload } from '~/types/generate'
import { getMusicDetail, convertWav, setIsPublic, setIsNotPublic, delClips } from '~/apis/app'
import { decryptData } from '~/utils/crypto'
import { useCommonStore } from '~/stores/commonStore'
import { usePlayerStore } from '~/stores/playerStore'
import { useAuthStore } from '~/stores/authStore'
import { useUpgradeModalStore } from '~/stores/upgradeModalStore'
import { useToastHandler } from '~/composables/useToastHandler'

/**
 * 通用的点赞/取消点赞功能
 * @param updateCallback - 更新回调函数，接收更新后的 isVote 和 upvote_count 值
 * @returns 返回 handleToggleUpvote 函数和 upvoteLoading 状态
 */
export function useToggleUpvote<T extends { uuid?: string, isVote?: number }>(
  updateCallback: (uuid: string, updates: { isVote: number, upvote_count: number }) => void | Promise<void>
) {
  const commonStore = useCommonStore()
  const upvoteLoading = ref<Record<string, boolean>>({})

  const handleToggleUpvote = async (song: T) => {
    if (!song.uuid || upvoteLoading.value[song.uuid]) return
    upvoteLoading.value[song.uuid] = true

    try {
      const isCurrentlyLiked = song.isVote === 1
      if (!isCurrentlyLiked) {
        await commonStore.addUpvoteCount({ uuid: song.uuid })
      } else {
        await commonStore.cutUpvoteCount({ uuid: song.uuid })
      }

      // 获取最新数据
      const { data } = await getMusicDetail({ uuids: song.uuid })
      const response = data as HomeGetMusicDetailResponse

      // 如果 clips 是加密字符串，尝试解密
      let clips: ProgressResponse[] = []
      if (response?.clips) {
        if (typeof response.clips === 'string') {
          try {
            clips = decryptData<ProgressResponse[]>(response.clips)
          } catch {
            try {
              clips = JSON.parse(response.clips) as ProgressResponse[]
            } catch {
              // 保持原样
            }
          }
        } else if (Array.isArray(response.clips)) {
          clips = response.clips
        }
      }

      // 更新数据（只更新 isVote 和 upvote_count）
      if (clips.length > 0) {
        const progressItem = clips[0]
        if (progressItem && typeof progressItem.isVote === 'number' && typeof progressItem.upvote_count === 'number') {
          const updates = {
            isVote: progressItem.isVote,
            upvote_count: progressItem.upvote_count
          }

          // 回调给调用方（当前列表 / 视图本地更新）
          await updateCallback(song.uuid, updates)

          // 全局广播点赞状态更新事件，方便其他视图（首页列表 / 我的歌曲 / 右侧详情 / 播放器）同步
          if (typeof window !== 'undefined') {
            try {
              window.dispatchEvent(new CustomEvent('song-upvote-updated', {
                detail: {
                  uuid: song.uuid,
                  ...updates
                }
              }))
            } catch (error) {
              console.warn('Failed to dispatch song-upvote-updated event:', error)
            }
          }
        }
      }
    } finally {
      upvoteLoading.value[song.uuid] = false
    }
  }

  return {
    handleToggleUpvote,
    upvoteLoading
  }
}

/**
 * 通用的生成/跳转逻辑（extend / cover / generate_similar）
 * @param payloadWrapSong - 是否自动将传入对象包装为 { song: payload }，默认为 true
 * @returns handleGenerateAction 方法
 */
export function useGenerateAction<T>() {
  const router = useRouter()
  const authStore = useAuthStore()
  const upgradeModalStore = useUpgradeModalStore()

  const emitPrefill = async (payload: any) => {
    if (typeof window !== 'undefined') {
      if (typeof (window as any).__setGenerateActiveTab === 'function') {
        ;(window as any).__setGenerateActiveTab('Custom')
      }
      await nextTick()
      window.dispatchEvent(new CustomEvent('generate:apply-prefill', { detail: payload }))
    }
  }

  const handleGenerateAction = async (
    action: GeneratePrefillPayload['action'],
    payload?: T | null,
    options?: { wrapSong?: boolean }
  ) => {
    if (!payload) return

    // extend/cover 仅限订阅用户
    if ((action === 'extend' || action === 'cover') && (!authStore.isAuthenticated || !authStore.user?.subscribe)) {
      upgradeModalStore.openModal()
      return
    }

    const wrapSong = options?.wrapSong ?? true
    const normalizedPayload: any
      = wrapSong
        ? ('song' in (payload as any)
            ? { action, ...(payload as any) }
            : { action, song: payload as any })
        : { action, ...(payload as any) }

    const isOnGeneratePage = router.currentRoute.value.path === '/generate'

    if (isOnGeneratePage) {
      await emitPrefill(normalizedPayload)
      return
    }

    // 不在 /generate 页面：先存储数据，再跳转，最后切换页签
    if (typeof window !== 'undefined') {
      try {
        sessionStorage.setItem('generatePrefill', JSON.stringify(normalizedPayload))
      } catch (error) {
        console.error('Failed to store generate prefill:', error)
      }
    }

    await router.push('/generate')

    // 跳转后切换页签
    await emitPrefill(normalizedPayload)
  }

  return {
    handleGenerateAction
  }
}

/**
 * Mp3 / Wav 下载通用逻辑
 */
export function useDownloadActions<T>(options?: {
  getMp3Url?: (song: T) => string | undefined | null
  getClipId?: (song: T) => string | undefined | null
  getTitle?: (song: T) => string | undefined | null
}) {
  const authStore = useAuthStore()
  const upgradeModalStore = useUpgradeModalStore()
  const wavDownloadLoading = ref(false)
  const wavPollingCancelled = ref(false)

  const BASE64_LIKE_PATTERN = /^[A-Za-z0-9+/=]+$/
  const WAV_EXTENSION_PATTERN = /\.wav($|\?)/i
  const WAV_POLL_INTERVAL = 3000
  const WAV_POLL_TIMEOUT = 60 * 1000

  const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

  const tryParseJSON = (value: string) => {
    try {
      return JSON.parse(value)
    } catch {
      return null
    }
  }

  const tryDecryptPayload = (value: string) => {
    if (!BASE64_LIKE_PATTERN.test(value)) {
      return null
    }
    try {
      return decryptData(value)
    } catch {
      return null
    }
  }

  const normalizePayload = (value: unknown): any => {
    if (typeof value !== 'string') {
      return value
    }
    const trimmed = value.trim()
    if (!trimmed) {
      return ''
    }
    const parsed = tryParseJSON(trimmed)
    if (parsed !== null) {
      if (typeof parsed === 'string') {
        const nested = tryParseJSON(parsed)
        return nested !== null ? nested : parsed
      }
      return parsed
    }
    const decrypted = tryDecryptPayload(trimmed)
    if (decrypted !== null && decrypted !== undefined) {
      if (typeof decrypted === 'string') {
        const nested = tryParseJSON(decrypted)
        return nested !== null ? nested : decrypted
      }
      return decrypted
    }
    return trimmed
  }

  const findWavUrlInUnknown = (value: unknown): string | null => {
    if (!value) return null
    if (typeof value === 'string') {
      return WAV_EXTENSION_PATTERN.test(value) ? value : null
    }
    if (Array.isArray(value)) {
      for (const item of value) {
        const result = findWavUrlInUnknown(item)
        if (result) return result
      }
      return null
    }
    if (typeof value === 'object') {
      for (const item of Object.values(value as Record<string, unknown>)) {
        const result = findWavUrlInUnknown(item)
        if (result) return result
      }
    }
    return null
  }

  const findWavUrlInClip = (clip: ProgressResponse): string | null => {
    const fileInfo = normalizePayload(clip.fileInfo)
    const fileInfoUrl = findWavUrlInUnknown(fileInfo)
    if (fileInfoUrl) return fileInfoUrl
    const streamUrl = findWavUrlInUnknown(clip.stream_url)
    if (streamUrl) return streamUrl
    const metadataUrl = findWavUrlInUnknown(normalizePayload((clip as any).metadata))
    return metadataUrl
  }

  const normalizeUuidPayload = (value: unknown): string[] => {
    const normalized = normalizePayload(value)
    if (!normalized) return []
    if (typeof normalized === 'string') {
      return normalized.split(',').map(item => item.trim()).filter(Boolean)
    }
    if (Array.isArray(normalized)) {
      return normalized
        .map((item) => {
          if (typeof item === 'string') return item.trim()
          if (item && typeof item === 'object' && typeof (item as any).uuid === 'string') {
            return (item as any).uuid
          }
          return ''
        })
        .filter(Boolean)
    }
    if (typeof normalized === 'object') {
      if (Array.isArray((normalized as any).uuids)) {
        return normalizeUuidPayload((normalized as any).uuids)
      }
      if (typeof (normalized as any).uuids === 'string') {
        return (normalized as any).uuids.split(',').map((item: string) => item.trim()).filter(Boolean)
      }
      if (typeof (normalized as any).uuid === 'string') {
        return [(normalized as any).uuid]
      }
    }
    return []
  }

  const extractClipsFromResponse = (payload: unknown): ProgressResponse[] => {
    if (!payload) return []
    const normalized = normalizePayload((payload as HomeGetMusicDetailResponse)?.clips ?? payload)
    if (Array.isArray(normalized)) {
      return normalized.filter(item => item && typeof item === 'object') as ProgressResponse[]
    }
    return []
  }

  const triggerFileDownload = async (url: string, filename: string) => {
    if (typeof window === 'undefined') return
    const response = await fetch(url)
    if (!response.ok) return
    const blob = await response.blob()
    const objectUrl = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = objectUrl
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(objectUrl)
  }

  async function waitForWavUrl(uuids: string[]): Promise<string | null> {
    if (!uuids.length) return null
    const joinedUuids = uuids.join(',')
    const startedAt = Date.now()
    wavPollingCancelled.value = false
    while (!wavPollingCancelled.value && Date.now() - startedAt < WAV_POLL_TIMEOUT) {
      try {
        const { data } = await getMusicDetail({ uuids: joinedUuids })
        const clips = extractClipsFromResponse(data as HomeGetMusicDetailResponse)
        if (!clips.length) {
          await sleep(WAV_POLL_INTERVAL)
          continue
        }
        const failedClip = clips.find(clip => clip?.status === 4 || clip?.errormsg)
        if (failedClip) {
          console.error('WAV conversion failed:', failedClip.errormsg || 'Unknown error')
          return null
        }
        const completedClip = clips.find(clip => clip?.status === 3)
        if (completedClip) {
          const wavUrl = findWavUrlInClip(completedClip)
          if (wavUrl) {
            return wavUrl
          }
        }
      } catch (error) {
        console.error('Failed to poll WAV status:', error)
      }
      await sleep(WAV_POLL_INTERVAL)
    }
    return null
  }

  const handleDownloadMp3 = async (song?: T | null) => {
    if ((!authStore.isAuthenticated || !authStore.user?.subscribe)) {
      upgradeModalStore.openModal()
      return
    }
    if (!song) return
    const mp3Url = options?.getMp3Url
      ? options.getMp3Url(song) ?? undefined
      : (song as any)?.fileInfo?.mp3Url || (song as any)?.fileInfo?.cosUrl
    if (!mp3Url) return
    const safeTitle = (options?.getTitle?.(song) || (song as any)?.title || (song as any)?.fullPrompt?.gpt_description_prompt || 'song')
      .toString()
      .replace(/[\\/:*?"<>|]/g, '_')
    await triggerFileDownload(mp3Url, `${safeTitle}.mp3`)
  }

  const handleDownloadWav = async (song?: T | null) => {
    // extend/cover 仅限订阅用户
    if ((!authStore.isAuthenticated || !authStore.user?.subscribe)) {
      upgradeModalStore.openModal()
      return
    }
    if (!song || wavDownloadLoading.value) return
    const clipId = options?.getClipId
      ? options.getClipId(song)
      : (song as any)?.clip_id || (song as any)?.uuid
    if (!clipId) return
    wavDownloadLoading.value = true
    try {
      const { data } = await convertWav({ clip_id: clipId })
      const uuids = normalizeUuidPayload((data as any)?.uuids ?? data)
      if (!uuids.length) {
        console.warn('convertWav response did not include uuids')
        return
      }
      const wavUrl = await waitForWavUrl(uuids)
      if (!wavUrl) {
        console.warn('WAV download URL is not ready')
        return
      }
      const safeTitle = (options?.getTitle?.(song) || (song as any)?.title || (song as any)?.fullPrompt?.gpt_description_prompt || 'song')
        .toString()
        .replace(/[\\/:*?"<>|]/g, '_')
      await triggerFileDownload(wavUrl, `${safeTitle}.wav`)
    } finally {
      wavDownloadLoading.value = false
    }
  }

  const cancelWavPolling = () => {
    wavPollingCancelled.value = true
  }

  return {
    handleDownloadMp3,
    handleDownloadWav,
    wavDownloadLoading,
    cancelWavPolling
  }
}

/**
 * 通用编辑详情弹窗状态与预填逻辑
 */
export function useEditDetailsModal<T>() {
  const editModalOpen = ref(false)
  const currentSong = ref<T | null>(null)

  const openEditDetails = (song?: T | null) => {
    currentSong.value = song || null
    editModalOpen.value = true
  }

  const handleEditModalToggle = (value: boolean) => {
    editModalOpen.value = value
  }

  return {
    editModalOpen,
    currentSong,
    openEditDetails,
    handleEditModalToggle
  }
}

/**
 * 公布 / 取消公布通用逻辑
 */
export function usePublishAction<T>(options?: {
  getUuid?: (song?: T | null) => string | undefined
  getIsPublic?: (song?: T | null) => number | undefined
  onUpdated?: (isPublic: number) => void | Promise<void>
}) {
  const publishLoading = ref(false)

  const resolveUuid = (song?: T | null) => options?.getUuid ? options.getUuid(song) : (song as any)?.uuid
  const resolveIsPublic = (song?: T | null) => options?.getIsPublic ? options.getIsPublic(song) : (song as any)?.is_public

  const handlePublishAction = async (song?: T | null) => {
    const uuid = resolveUuid(song)
    if (!uuid || publishLoading.value) return false
    publishLoading.value = true
    try {
      const isPublished = resolveIsPublic(song) === 1
      if (isPublished) {
        await setIsNotPublic({ uuid })
        await options?.onUpdated?.(0)
      } else {
        await setIsPublic({ uuid })
        await options?.onUpdated?.(1)
      }
      return true
    } finally {
      publishLoading.value = false
    }
  }

  return {
    publishLoading,
    handlePublishAction
  }
}

/**
 * 通用的分享弹窗状态与处理
 * @param getPayload 自定义从歌曲中提取分享所需字段的函数
 */
export function useShareModal<T extends { uuid?: string, title?: string }>(
  getPayload?: (song: T) => { uuid?: string, title?: string }
) {
  const shareModalOpen = ref(false)
  const currentShareSong = ref<{ uuid?: string, title?: string } | null>(null)

  const handleShare = (song?: T) => {
    if (song) {
      const payload = getPayload
        ? getPayload(song)
        : { uuid: song.uuid, title: (song as any)?.title }
      currentShareSong.value = payload
    }
    shareModalOpen.value = true
  }

  return {
    shareModalOpen,
    currentShareSong,
    handleShare
  }
}

/**
 * 删除 / 移动到回收站通用逻辑
 */
export function useDeleteAction<T>(options?: {
  getUuid?: (song?: T | null) => string | undefined
  onDeleted?: (uuid: string) => void | Promise<void>
}) {
  const deleteLoading = ref(false)
  const playerStore = usePlayerStore()
  const { showSuccessToast } = useToastHandler()

  const deleteConfirmOpen = ref(false)
  const pendingSong = ref<T | null>(null)

  const resolveUuid = (song?: T | null) => options?.getUuid ? options.getUuid(song) : (song as any)?.uuid

  const handleDeleteAction = (song?: T | null) => {
    if (deleteLoading.value) return false
    pendingSong.value = song || null
    deleteConfirmOpen.value = true
    return false
  }

  const handleCancelDelete = () => {
    if (deleteLoading.value) return
    deleteConfirmOpen.value = false
    pendingSong.value = null
  }

  const handleConfirmDelete = async () => {
    const uuid = resolveUuid(pendingSong.value || undefined)
    if (!uuid || deleteLoading.value) return false
    deleteLoading.value = true
    try {
      await delClips({
        isDelete: '1',
        uuids: uuid
      })
      await options?.onDeleted?.(uuid)
      // 如果删除的是当前正在播放的歌曲，需要同步更新播放器状态（切歌并清除信息）
      if (playerStore && typeof playerStore.handleSongDeleted === 'function') {
        await playerStore.handleSongDeleted(uuid)
      }
      showSuccessToast('Deleted successfully.')
      deleteConfirmOpen.value = false
      pendingSong.value = null
      return true
    } finally {
      deleteLoading.value = false
    }
  }

  return {
    deleteLoading,
    deleteConfirmOpen,
    handleDeleteAction,
    handleConfirmDelete,
    handleCancelDelete
  }
}
